import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import programService from '@/shared/services/programService';
import LoadError from '@/components/common/LoadError';
import LoadingState from '@/components/common/LoadingState';
import useProgramAvailability from '@/shared/hooks/useProgramAvailability';
import { formatWindowDate, releaseMoment } from '@/shared/utils/deadline';
import { DRAFTS_SHOW, listSearch } from './overviewLists';
import './CoordinatorOverview.css';

const plural = (count, one, many) => (count === 1 ? one : many);
const unit = (count, noun) => `${count} ${plural(count, noun, `${noun}s`)}`;
const inSections = (sections) => `, in ${unit(sections, 'section')}`;

// "Nov 2 at 8:00 AM", always Mountain time
function shortReleaseText(stored) {
    const moment = releaseMoment(stored);
    if (!moment) return '';
    const day = moment.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'America/Denver',
    });
    const time = moment.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'America/Denver',
    });
    return `${day} at ${time}`;
}

function waitingReason({ reason, date }) {
    if (reason === 'review_closed') {
        return `the review window closed on ${formatWindowDate(date)}`;
    }
    if (reason === 'release_soon') {
        return `decisions are released on ${shortReleaseText(date)}`;
    }
    return `decisions were released on ${shortReleaseText(date)}`;
}

// Each item's sentence and the list it opens. An item whose list is not on
// this dashboard (SI has no Positions tab) is not shown.
const ITEMS = {
    offers_expired: {
        target: 'positions',
        urgent: true,
        sentence: ({ count, sections }) =>
            `${unit(count, 'offer')} expired without an answer and ${plural(count, 'needs', 'need')} your choice${inSections(sections)}`,
    },
    offers_expiring: {
        target: 'positions',
        sentence: ({ count, sections }) =>
            `${unit(count, 'offer')} ${plural(count, 'expires', 'expire')} in the next 3 days${inSections(sections)}`,
    },
    requests_waiting: {
        target: 'requests',
        show: { requests: 'waiting', ta: 'pending' },
        sentence: (item) =>
            `${unit(item.count, 'request')} ${plural(item.count, 'is', 'are')} still waiting for a decision, and ${waitingReason(item)}`,
    },
    drafts_unsubmitted: {
        target: 'requests',
        show: { requests: DRAFTS_SHOW, ta: DRAFTS_SHOW },
        sentence: ({ count, closeDate }) =>
            `${unit(count, 'request')} started but not submitted. The window closes ${formatWindowDate(closeDate)}.`,
    },
    review_waiting: {
        target: 'positions',
        sentence: ({ count, sections }) =>
            `${unit(count, 'student')} ${plural(count, 'has', 'have')} waited 7 days or more for review${inSections(sections)}`,
    },
    no_applicants: {
        target: 'positions',
        sentence: ({ sections }) =>
            `${unit(sections, 'section')} ${plural(sections, 'has', 'have')} places available and no applicants after two weeks`,
    },
};

const REQUEST_PARTS = [
    ['approved', 'approved'],
    ['provisional', 'provisional'],
    ['waitlisted', 'waitlisted'],
    ['not_selected', 'not selected'],
    ['waiting', 'waiting'],
    ['revision', 'out for revision'],
    ['elsewhere', 'supported by another program'],
    ['withdrawn', 'withdrawn'],
];

const TA_REQUEST_PARTS = [
    ['approved', 'approved'],
    ['denied', 'declined'],
    ['pending', 'waiting'],
    ['cancelled', 'cancelled'],
    ['converted', 'converted'],
];

// A switched-off program takes no new requests, but the ones already waiting
// on it are still its to decide (the server refuses only new requests), so
// they are named here rather than left to "Nothing needs your attention"
function waitingWhileOffItem({ data, programType, switchedOff }) {
    if (!switchedOff) return null;
    if (data.attention.some((item) => item.kind === 'requests_waiting')) {
        return null;
    }
    const show = programType === 'ta' ? 'pending' : 'waiting';
    const count = data.requests.counts[show] || 0;
    if (count === 0) return null;
    const program = programType.toUpperCase();
    return {
        show,
        sentence: `${unit(count, 'request')} ${plural(count, 'is', 'are')} still waiting for ${programType === 'ta' ? 'a' : 'an'} ${program} decision. ${program} is not taking new requests through the Hub this term, and you can still decide ${plural(count, 'it', 'them')}.`,
    };
}

function AttentionBlock({ items, tabs, programType, offItem }) {
    const shown = items.filter((item) => {
        const config = ITEMS[item.kind];
        return config && tabs[config.target];
    });
    return (
        <section
            className="coordinator-overview-block"
            aria-labelledby="overview-attention"
        >
            <h2 id="overview-attention" className="h5">
                Needs your attention
            </h2>
            {shown.length === 0 && !offItem ? (
                <p className="mb-0">Nothing needs your attention right now.</p>
            ) : (
                <ul className="list-unstyled mb-0">
                    {offItem && (
                        <li className="coordinator-overview-item">
                            <Link to={listSearch(tabs.requests, offItem.show)}>
                                {offItem.sentence}
                            </Link>
                        </li>
                    )}
                    {shown.map((item) => {
                        const config = ITEMS[item.kind];
                        const show = config.show
                            ? config.show[
                                  programType === 'ta' ? 'ta' : 'requests'
                              ]
                            : item.kind;
                        return (
                            <li
                                key={item.kind}
                                className="coordinator-overview-item"
                            >
                                {config.urgent && (
                                    <Badge
                                        bg="danger"
                                        className="me-2 text-nowrap"
                                    >
                                        Past due
                                    </Badge>
                                )}
                                <Link
                                    to={listSearch(tabs[config.target], show)}
                                >
                                    {config.sentence(item)}
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}

function PlacesBar({ hiring }) {
    const {
        accepted,
        awaitingAnswer,
        offersExpired,
        available,
        closedByDecline,
    } = hiring;
    // A place can be counted twice only if an award was cut below the places
    // already held; the bar then scales to what it shows.
    const total = Math.max(
        hiring.awarded,
        accepted + awaitingAnswer + offersExpired + available + closedByDecline
    );
    const segments = [
        ['accepted', accepted],
        ['awaiting', awaitingAnswer],
        ['expired', offersExpired],
        ['available', available],
        ['closed', closedByDecline],
    ].filter(([, value]) => value > 0);
    return (
        <div
            className="progress-stacked coordinator-overview-bar mb-2"
            aria-hidden="true"
        >
            {segments.map(([key, value]) => (
                <div
                    key={key}
                    className={`progress coordinator-overview-segment segment-${key}`}
                    style={{ width: `${(value / total) * 100}%` }}
                >
                    <div className="progress-bar" />
                </div>
            ))}
        </div>
    );
}

function HiringBlock({ hiring, termName, decisionsReleased, positionsTab }) {
    const {
        awarded,
        accepted,
        awaitingAnswer,
        offersExpired,
        available,
        closedByDecline,
    } = hiring;
    const provisional = hiring.provisional;
    const legend = [
        ['accepted', `${accepted} accepted`],
        ['awaiting', `${awaitingAnswer} awaiting an answer`],
        ...(offersExpired > 0
            ? [
                  [
                      'expired',
                      `${offersExpired} offer${offersExpired === 1 ? '' : 's'} expired`,
                  ],
              ]
            : []),
        ['available', `${available} available`],
        ...(closedByDecline > 0
            ? [['closed', `${closedByDecline} closed after a nominee declined`]]
            : []),
    ];
    return (
        <section
            className="coordinator-overview-block"
            aria-labelledby="overview-hiring"
        >
            <h2 id="overview-hiring" className="h5">
                Hiring progress
            </h2>
            {awarded === 0 && provisional.places === 0 && (
                <p className="mb-2">{`No places awarded for ${termName} yet.`}</p>
            )}
            {awarded > 0 && (
                <>
                    <p className="fs-4 fw-semibold mb-2">
                        {`${accepted} of ${unit(awarded, 'place')} filled`}
                    </p>
                    <PlacesBar hiring={hiring} />
                    <p className="mb-2">
                        {`${unit(awarded, 'place')} awarded: `}
                        {/* Each part stays on one line; the space after
                            it is where a narrow screen wraps */}
                        {legend.map(([key, text], index) => (
                            <React.Fragment key={key}>
                                <span className="text-nowrap">
                                    <span
                                        className={`coordinator-overview-swatch segment-${key}`}
                                        aria-hidden="true"
                                    />
                                    {text}
                                    {index < legend.length - 1 ? ',' : '.'}
                                </span>{' '}
                            </React.Fragment>
                        ))}
                    </p>
                </>
            )}
            {provisional.places > 0 &&
                (hiring.released ? (
                    <p className="mb-2">
                        {`${unit(provisional.places, 'more place')} in ${unit(provisional.sections, 'section')} ${plural(provisional.places, 'is', 'are')} provisional: ${plural(provisional.sections, 'its contingent award is', 'their contingent awards are')} not final yet.`}
                    </p>
                ) : (
                    <p className="mb-2">
                        {`${unit(provisional.places, 'place')} in ${unit(provisional.sections, 'section')} ${plural(provisional.places, 'is', 'are')} awarded and held until decisions are released on ${shortReleaseText(decisionsReleased)}. Hiring starts then.`}
                    </p>
                ))}
            {hiring.sections + provisional.sections > 0 && (
                <Link to={listSearch(positionsTab)}>
                    {`See ${unit(hiring.sections + provisional.sections, 'section')} in Positions`}
                </Link>
            )}
        </section>
    );
}

function RequestsBlock({
    requests,
    drafts,
    programType,
    termName,
    requestsTab,
}) {
    const parts = (programType === 'ta' ? TA_REQUEST_PARTS : REQUEST_PARTS)
        .map(([key, label]) => [key, label, requests.counts[key] || 0])
        .filter(([, , count]) => count > 0);
    return (
        <section
            className="coordinator-overview-block"
            aria-labelledby="overview-requests"
        >
            <h2 id="overview-requests" className="h5">
                Requests
            </h2>
            {requests.total === 0 ? (
                <p className="mb-0">{`No requests for ${termName} yet.`}</p>
            ) : (
                <p className="fs-5 mb-0">
                    <Link to={listSearch(requestsTab)}>
                        {unit(requests.total, 'request')}
                    </Link>
                    {': '}
                    {parts.map(([key, label, count], index) => (
                        <React.Fragment key={key}>
                            <Link
                                className="text-nowrap"
                                to={listSearch(requestsTab, key)}
                            >
                                {`${count} ${label}`}
                            </Link>
                            {index < parts.length - 1 ? ', ' : '.'}
                        </React.Fragment>
                    ))}
                </p>
            )}
            {drafts?.count > 0 && (
                <p className="mt-2 mb-0 text-muted">
                    <Link to={listSearch(requestsTab, DRAFTS_SHOW)}>
                        {`${drafts.count}${requests.total > 0 ? ' more' : ''} started but not submitted`}
                    </Link>
                </p>
            )}
        </section>
    );
}

/**
 * The Overview tab of a coordinator dashboard: what needs the coordinator
 * now, how far hiring is, and where the requests stand, for one term. Every
 * number links to the tab that lists it, filtered to exactly those rows.
 * `tabs` names this dashboard's tab for `requests` and, when it has one, for
 * `positions`.
 */
export default function CoordinatorOverview({
    programType,
    term,
    termName,
    tabs,
    active = true,
}) {
    const [state, setState] = useState({ status: 'loading' });
    const request = useRef(0);
    const switchedOff = useProgramAvailability()?.[programType] === false;

    const load = useCallback(
        async ({ keepShown = false } = {}) => {
            const mine = ++request.current;
            // A refresh keeps the numbers on screen until the new ones land
            if (!keepShown) setState({ status: 'loading' });
            try {
                const data = await programService.getOverview(
                    programType,
                    term
                );
                if (mine === request.current) {
                    setState({ status: 'ready', data });
                }
            } catch (_err) {
                if (mine === request.current) setState({ status: 'error' });
            }
        },
        [programType, term]
    );

    // The dashboard keeps this tab mounted after it is left, so what the
    // coordinator did on another tab (an offer, a decision) is read again
    // each time the tab becomes active
    const shownFor = useRef(null);
    useEffect(() => {
        if (!term || !active) return undefined;
        const sameTerm = shownFor.current === `${programType}:${term}`;
        shownFor.current = `${programType}:${term}`;
        load({ keepShown: sameTerm });
        return () => {
            request.current += 1;
        };
    }, [term, programType, active, load]);

    if (!term) {
        return <p className="text-muted">Choose a term to see its overview.</p>;
    }
    if (state.status === 'loading') {
        return <LoadingState rows={3} label="Loading the overview" />;
    }
    if (state.status === 'error') {
        return <LoadError what="the overview" onRetry={load} />;
    }

    const { data } = state;
    const label = termName || term;
    return (
        <div
            className={`coordinator-overview coordinator-overview-${programType}`}
        >
            <AttentionBlock
                items={data.attention}
                tabs={tabs}
                programType={programType}
                offItem={waitingWhileOffItem({
                    data,
                    programType,
                    switchedOff,
                })}
            />
            {tabs.positions && data.hiring && (
                <HiringBlock
                    hiring={data.hiring}
                    termName={label}
                    decisionsReleased={data.decisionsReleased}
                    positionsTab={tabs.positions}
                />
            )}
            <RequestsBlock
                requests={data.requests}
                drafts={data.drafts}
                programType={programType}
                termName={label}
                requestsTab={tabs.requests}
            />
        </div>
    );
}
